import { Box, InputAdornment, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { FormContainer } from '../../containers/FormContainer/FormContainer';
import { Copy } from '../../copy/Copy';
import { marginTop16Sx, buttonSx } from '../../styles/Global.style';
import { Form } from '../Form/Form';
import { FormInput } from '../FormInput/FormInput';
import { Button } from '../Button/Button';
import { useAuth } from '../../hooks/useAuth/useAuth';
import {
  ADD_ACTIVITY_INIT_VALUES,
  ADD_ACTIVITY_SELECT_OPTIONS,
} from '../../constants/activity';
import { ADD_ACTIVITY_VALIDATION_SCHEMA } from '../../validation/activity';
import { saveActivity } from '../../helpers/saveActivity';
import { FormSelect } from '../FormSelect/FormSelect';
import { ToggleSwitch } from '../ToggleSwitch/ToggleSwitch';
import {
  distanceInputAdornmentSx,
  moodCheckSx,
  toggleSwitchContainerSx,
} from './AddActivityForm.style';
import { kmToMiles } from '../../utils/kmToMiles';
import { APP_PATHS } from '../../constants/paths';
import { MoodCheck } from '../MoodCheck/MoodCheck';
import { dateObjToYMD } from '../../utils/dateObjToYMD';
import { DateInput } from '../DateInput/DateInput';
import { Loading } from '../Loading/Loading';

export const AddActivityForm = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [message, setMessage] = useState();
  const [isError, setIsError] = useState(false);
  const [isKm, setIsKm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const distanceUnits = isKm
    ? Copy.generate('Inputs.km')
    : Copy.generate('Inputs.miles');

  const handleToggleSwitch = (e) => {
    setIsKm(e.target.checked);
  };

  const resetError = () => {
    setTimeout(() => {
      setMessage('');
      setIsError(false);
    }, 3000);
  };

  const formik = useFormik({
    initialValues: ADD_ACTIVITY_INIT_VALUES,
    validationSchema: ADD_ACTIVITY_VALIDATION_SCHEMA,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (val) => {
      setIsLoading(true);

      try {
        await saveActivity({
          data: {
            userId: user.id,
            date: dateObjToYMD(val.date),
            distance: isKm ? kmToMiles(val.distance) : val.distance,
            activity: val.activity,
            mood: val.mood ? parseInt(val.mood) : 0,
          },
          jwt: user?.jwt,
        });

        location.href = APP_PATHS.profile;
      } catch (err) {
        setIsError(true);
        setMessage(err.message);
        resetError();
      }
    },
  });

  return !isLoading ? (
    <FormContainer>
      <Form formik={formik} isError={isError} message={message}>
        <DateInput formik={formik} />
        <FormInput
          inputFor="distance"
          formik={formik}
          labelText={Copy.generate('Inputs.distance')}
          inputType="number"
          InputProps={{
            endAdornment: (
              <InputAdornment
                sx={distanceInputAdornmentSx(theme)}
                position="end"
              >
                {distanceUnits}
              </InputAdornment>
            ),
          }}
        />

        <FormSelect
          selectFor="activity"
          formik={formik}
          options={ADD_ACTIVITY_SELECT_OPTIONS}
          labelText={Copy.generate('Inputs.activity')}
        />

        <MoodCheck
          formik={formik}
          label={Copy.generate('Inputs.howYouFeeling')}
          sx={moodCheckSx(theme)}
        />

        <Button
          type="submit"
          fullWidth
          text={Copy.generate('Global.add')}
          sx={{ ...buttonSx(theme), ...marginTop16Sx }}
        />
      </Form>

      <Box component="div" sx={toggleSwitchContainerSx}>
        <ToggleSwitch
          leftText={Copy.generate('Inputs.miles')}
          rightText={Copy.generate('Inputs.km')}
          handleChange={handleToggleSwitch}
          checked={isKm}
        />
      </Box>
    </FormContainer>
  ) : (
    <Loading />
  );
};
